import { useState } from "react";
import { FileSystemItem } from "types";
import { FolderListView } from "components/file-system/folder-view/FolderListView";
import { useConfirmation } from "components/dialog/confirmation";
import SearchAndNew from "components/file-system/SearchAndNewFileOrFolder";
import { Loader2, Play, Upload, X } from "lucide-react";
import { Dialog } from "components/dialog";
import { MdOutlineCloudUpload } from "react-icons/md";
import { useDropzone } from "react-dropzone";
import { handleAxiosAndCaughtError } from "utils";
import { useAgentEvaluation } from "../../../../../layouts/agent/agent-info/agent-evaluate/context/AgentEvaluationContext";
import toast from "react-hot-toast";
import api from "api/axios";
import { useTranslation } from "hooks/useTranslation";

const DatasetView = () => {
  const { datasets, uploadDatasets, deleteDataset, evaluateAgent } =
    useAgentEvaluation();
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { confirm } = useConfirmation();
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      setUploadFiles((prev) => [...prev, ...acceptedFiles]);
      setUploadError(null);
    },
    onDropRejected: (rejections) => {
      const error = rejections[0]?.errors[0]?.message;
      setUploadError(error || t("library.errorUploadingFile"));
    },
    accept: {
      "text/csv": [".csv"],
    },
    maxSize: 20 * 1024 * 1024, // 20MB
  });

  const handleEvaluate = async (file: FileSystemItem) => {
    const confirmed = await confirm({
      title: t("agents.evaluateSection.evaluateDataset"),
      message: t("agents.evaluateSection.evaluateDatasetConfirm", { name: file.name }),
    });

    if (confirmed) {
      await evaluateAgent(file.id);
    }
  };

  const handleDelete = async (fileToDelete: FileSystemItem) => {
    const confirmed = await confirm({
      title: t("agents.evaluateSection.deleteFile"),
      message: t("agents.evaluateSection.deleteFileConfirm", { name: fileToDelete.name }),
      variant: "danger",
    });

    if (confirmed) {
      await deleteDataset(fileToDelete.id);
    }
  };

  const filteredItems = datasets.filter((f) =>
    f.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploadError(null);
    setIsUploading(true);

    try {
      if (uploadFiles.length === 0) {
        setUploadError(t("library.selectAtLeastOneFile"));
        return;
      }
      await uploadDatasets(uploadFiles);

      setIsOpenDialog(false);
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await api.get(`/templates/evaluation_dataset.csv`, {
        responseType: "blob",
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.target = "_blank";
      link.href = url;
      link.download = "evaluation_dataset.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t("agents.evaluateSection.failedToDownloadTemplate"));
    }
  };

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex-1">
          <SearchAndNew
            className="!mx-0 mt-4 md:mb-0"
            value={searchValue}
            onChange={(s) => setSearchValue(s)}
            showAdd={false}
            header={<div className="flex w-full gap-2">
              <p>
                {t("agents.evaluateSection.downloadEvaluationTemplateDesc")}
              </p>
              <button onClick={handleDownload} className="text-brand-500">
                {t("agents.evaluateSection.downloadEvaluationTemplate")}
              </button>
            </div>}
          >
            <Dialog
              setIsOpen={setIsOpenDialog}
              isOpen={isOpenDialog}
              header={t("agents.evaluateSection.newDocuments")}
              subHeader={t("agents.evaluateSection.uploadCSVFiles")}
              children={
                <div className="  md:w-[30vw]">
                  <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    {uploadError && (
                      <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                        {uploadError}
                      </div>
                    )}
                    <div className="flex flex-col gap-6">
                      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex-1">
                          <div
                            {...getRootProps()}
                            className={`flex w-full  cursor-pointer flex-col items-center justify-center rounded-xl border-[1px] border-dashed border-gray-200 bg-gray-100 py-5 dark:border-gray-700 dark:bg-navy-700 sm:w-full ${
                              isUploading ? "cursor-not-allowed opacity-50" : ""
                            }`}
                          >
                            <input
                              {...getInputProps()}
                              disabled={isUploading}
                            />
                            <MdOutlineCloudUpload className="mb-2 text-[30px] text-brand-500 dark:text-white" />
                            <div className="text-center">
                              <p className="text-base font-semibold text-navy-700 dark:text-white">
                                {t("library.dragAndDropFiles")}
                              </p>
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                {t("library.clickToSelectCSV")}
                              </p>
                            </div>
                            <button
                              type="button"
                              className="mt-3 rounded border border-brand-500 bg-white px-4 py-1 text-xs font-semibold text-brand-500 transition hover:bg-brand-50 dark:border-brand-400 dark:bg-navy-800 dark:text-brand-400 dark:hover:bg-navy-700"
                              disabled={isUploading}
                            >
                              {t("library.browseFiles")}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Uploaded Files List */}
                      <div className="max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-navy-700">
                        {uploadFiles.length === 0 ? (
                          <div className="flex items-center justify-center py-10 text-center">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {t("library.noFilesAdded")}
                            </p>
                          </div>
                        ) : (
                          uploadFiles.map((file, idx) => (
                            <div
                              key={`${file.name}-${idx}`}
                              className="flex items-center justify-between border-b border-gray-100 py-2 last:border-b-0 dark:border-gray-700"
                            >
                              <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-brand-500 dark:bg-navy-800 dark:text-brand-400">
                                  <MdOutlineCloudUpload />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-navy-700 dark:text-white">
                                    {file.name}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="text-gray-400 transition hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                                title={t("library.remove")}
                                onClick={() => {
                                  setUploadFiles((prev) =>
                                    prev.filter((_, i) => i !== idx)
                                  );
                                  setUploadError(null);
                                }}
                                disabled={isUploading}
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ))
                        )}
                      </div>

                      <div className=" rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-navy-800">
                        <div className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          {t("library.tipsForUploading")}
                        </div>
                        <ul className="list-disc space-y-1 pl-5 text-xs text-gray-600 dark:text-gray-400">
                          <li>{t("library.supportedFileTypesCSV")}</li>
                          <li>{t("library.maxFileSize")}</li>
                          <li>
                            {t("library.ensureNoPII")}
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="mt-auto flex justify-end gap-2 ">
                      <button
                        type="button"
                        className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-navy-600"
                        onClick={() => setIsOpenDialog(false)}
                        disabled={isUploading}
                      >
                        {t("common.cancel")}
                      </button>
                      <button
                        type="submit"
                        className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-400 dark:hover:bg-brand-500"
                        disabled={isUploading || uploadFiles.length === 0}
                      >
                        {isUploading ? (
                          <span className="flex items-center gap-2">
                            <Loader2 size={16} className="animate-spin" />
                            {t("library.uploading")}
                          </span>
                        ) : (
                          t("library.upload")
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              }
              trigger={
                <div className="flex w-full flex-shrink-0 flex-col gap-2 md:w-auto md:flex-row">
                  <button className="flex h-[40px] w-full cursor-pointer items-center justify-center gap-2 rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 md:w-auto">
                    <Upload size={15} />
                    <span>{t("library.upload")}</span>
                  </button>
                </div>
              }
            />
          </SearchAndNew>
        </div>
      </div>
      {filteredItems.length > 0 ? (
        <div className="overflow-auto">
          <FolderListView
            fileItems={filteredItems}
            handleDelete={handleDelete}
            willUpdateUrl={false}
            otherActions={[
              {
                label: t("agents.evaluate"),
                icon: <Play size={15} />,
                onClick: handleEvaluate,
              },
            ]}
          />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border bg-white p-12 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {searchValue
              ? t("agents.evaluateSection.noMatchingFiles")
              : t("agents.evaluateSection.folderIsEmpty")}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {searchValue
              ? t("agents.evaluateSection.tryDifferentSearch")
              : t("agents.evaluateSection.createNewFile")}
          </p>
        </div>
      )}
    </>
  );
};

export default DatasetView;
